<script lang="ts">
	import { browser } from '$app/env';
	import Portal from '#lib/modern/Portal.svelte';
	import { isMobile } from '#lib/stores/mobile.js';
	import { onDestroy, onMount } from 'svelte';

	interface Props {
		align?: 'left' | 'right' | 'center';
		valign?: 'top' | 'bottom' | 'center';
		marginTop?: number;
		marginLeft?: number;
		targetElement: HTMLElement | string;
		children?: import('svelte').Snippet;
		onclose?: () => void;
		zIndex?: string;
	}

	let {
		align = 'center',
		valign = 'bottom',
		marginTop = 0,
		marginLeft = 0,
		targetElement,
		children,
		onclose = () => {},
		zIndex = 'z-50'
	}: Props = $props();

	let element: HTMLElement | undefined = $derived(getElement(targetElement));
	let computedValign = $state<'top' | 'bottom' | 'center' | null>(null);
	let effectiveValign = $derived(computedValign ?? valign);
	let transformOrigin = $derived(
		`${effectiveValign === 'top' ? 'bottom' : 'top'} ${align === 'center' ? 'center' : align}`
	);

	let padding = 8; // avoid sticking to the edges
	let popin: HTMLDivElement | undefined = $state();
	let mounted = $state(false);
	let closing = $state(false);
	let resizeObserver: ResizeObserver | undefined;
	let rafId: number | undefined;

	function getElement(target: string | HTMLElement | undefined): HTMLElement | undefined {
		if (!browser) return;

		if (typeof target === 'string') {
			return document.getElementById(target) || undefined;
		} else if (target === undefined) {
			// Return body
			return document.getElementById('app-layout') || undefined;
		}
		return target;
	}

	function scheduleUpdate() {
		if (!browser || closing) return;
		if (rafId) cancelAnimationFrame(rafId);
		rafId = requestAnimationFrame(() => {
			update();
		});
	}

	function update() {
		const screen = getElement(undefined);
		if (!screen || !popin) return;

		const popinWidth = popin.offsetWidth;
		const popinHeight = popin.offsetHeight;

		if (popinWidth === 0 || popinHeight === 0) {
			scheduleUpdate();
			return;
		}

		if (!element) {
			return;
		}

		const screenRect = screen.getBoundingClientRect();
		const targetRect = element.getBoundingClientRect();

		const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
		const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

		const minLeft = Math.max(0, screenRect.left + ($isMobile ? 0 : 64)) + padding;
		const maxRight = Math.min(viewportWidth, screenRect.right) - padding;
		const minTop = Math.max(0, screenRect.top + ($isMobile ? 68 : 6)) + padding;
		const maxBottom = Math.min(viewportHeight, screenRect.bottom) - padding;

		const availableWidth = Math.max(0, maxRight - minLeft);
		const availableHeight = Math.max(0, maxBottom - minTop);

		popin.style.maxWidth = `${availableWidth}px`;
		popin.style.maxHeight = `${availableHeight}px`;

		// Move X popin to the target
		let popinLeft =
			align === 'left'
				? marginLeft + targetRect.left
				: align === 'right'
					? marginLeft + targetRect.right - popinWidth
					: // Center
						marginLeft + targetRect.left + targetRect.width / 2 - popinWidth / 2;

		// Clamp within screen bounds
		popinLeft = Math.max(minLeft, Math.min(popinLeft, maxRight - popinWidth));
		popin.style.left = `${popinLeft}px`;

		// Auto-flip Y position if overflowing viewport
		let currentValign = valign;
		const spaceBelow = maxBottom - (targetRect.bottom + marginTop);
		const spaceAbove = targetRect.top - marginTop - minTop;

		if (valign === 'bottom') {
			if (
				spaceBelow < popinHeight &&
				(spaceAbove >= popinHeight || spaceAbove > spaceBelow)
			) {
				currentValign = 'top';
			}
		} else if (valign === 'top') {
			if (
				spaceAbove < popinHeight &&
				(spaceBelow >= popinHeight || spaceBelow > spaceAbove)
			) {
				currentValign = 'bottom';
			}
		}
		computedValign = currentValign;

		// Move Y popin to the target
		let popinTop =
			currentValign === 'bottom'
				? marginTop + targetRect.bottom
				: currentValign === 'top'
					? targetRect.top - marginTop - popinHeight
					: // Center
						marginTop + targetRect.top + targetRect.height / 2 - popinHeight / 2;

		// Clamp within screen bounds
		popinTop = Math.max(minTop, Math.min(popinTop, maxBottom - popinHeight));
		popin.style.top = `${popinTop}px`;

		mounted = true;
	}

	let isInteractive = $state(false);
	let interactiveTimer: ReturnType<typeof setTimeout> | null = null;

	onMount(() => {
		window.addEventListener('resize', scheduleUpdate);
		window.addEventListener('scroll', scheduleUpdate, true);
		document.addEventListener('scroll', scheduleUpdate, true);

		if (popin && typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(() => {
				scheduleUpdate();
			});
			resizeObserver.observe(popin);
		}

		update();

		interactiveTimer = setTimeout(() => {
			isInteractive = true;
		}, 200);
	});

	let closeTimer: ReturnType<typeof setTimeout> | null = null;

	onDestroy(() => {
		close();
		if (closeTimer) clearTimeout(closeTimer);
		if (interactiveTimer) clearTimeout(interactiveTimer);
		if (browser) {
			if (rafId) cancelAnimationFrame(rafId);
			window.removeEventListener('resize', scheduleUpdate);
			window.removeEventListener('scroll', scheduleUpdate, true);
			document.removeEventListener('scroll', scheduleUpdate, true);
			resizeObserver?.disconnect();
		}
	});

	function close() {
		if (closing) return;
		closing = true;
		if (closeTimer) clearTimeout(closeTimer);
		closeTimer = setTimeout(() => {
			onclose();
		}, 160);
	}

	function isInsideElement(x: number, y: number, element: HTMLElement | undefined): boolean {
		if (!element) return false;
		const rect = element.getBoundingClientRect();
		return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
	}

	function isPasswordManagerElement(e: MouseEvent): boolean {
		const path = e.composedPath();
		const passwordManagerKeywords = [
			'dashlane',
			'bitwarden',
			'1password',
			'onepassword',
			'lastpass',
			'protonpass',
			'proton-pass',
			'nordpass',
			'keeper',
			'roboform',
			'enpass',
			'autofill'
		];

		for (const item of path) {
			if (item instanceof HTMLElement || item instanceof SVGElement) {
				const tagName = item.tagName.toLowerCase();
				const id = item.id ? item.id.toLowerCase() : '';
				const className =
					typeof item.className === 'string' ? item.className.toLowerCase() : '';

				for (const kw of passwordManagerKeywords) {
					if (
						tagName.includes(kw) ||
						id.includes(kw) ||
						className.includes(kw) ||
						item.hasAttribute(`data-${kw}`) ||
						item.hasAttribute(`data-${kw}-ignore`)
					) {
						return true;
					}
				}
			}
		}

		const target = e.target as Element | null;
		if (target && target instanceof Element) {
			const tagName = target.tagName.toLowerCase();
			const id = target.id.toLowerCase();
			const className =
				typeof target.className === 'string' ? target.className.toLowerCase() : '';
			for (const kw of passwordManagerKeywords) {
				if (
					tagName.includes(kw) ||
					id.includes(kw) ||
					className.includes(kw) ||
					target.hasAttribute(`data-${kw}`) ||
					target.hasAttribute(`data-${kw}-ignore`)
				) {
					return true;
				}
			}
		}

		return false;
	}

	function handleWindowClick(e: MouseEvent) {
		if (e.clientX === 0 && e.clientY === 0) return;

		// Ignore clicks during initial mounting grace period (prevents premature closure)
		if (!isInteractive) return;

		// If target element is a zero-size positioning anchor, resolve to parent container
		const target =
			element && element.offsetWidth === 0 && element.offsetHeight === 0
				? element.parentElement
				: element;

		// Don't close if click target is inside popin or target element / container
		if (
			popin?.contains(e.target as Node) ||
			target?.contains(e.target as Node) ||
			e.composedPath().includes(popin!) ||
			(target && e.composedPath().includes(target))
		) {
			return;
		}

		// Don't close if click is within geometric bounding rect
		if (
			isInsideElement(e.clientX, e.clientY, popin) ||
			(target && isInsideElement(e.clientX, e.clientY, target))
		) {
			return;
		}

		// Don't close if click is from a password manager or autofill extension (Dashlane, Bitwarden, etc.)
		if (isPasswordManagerElement(e)) {
			return;
		}

		close();
	}
</script>

<svelte:window onclick={handleWindowClick} />

<Portal
	bind:container={popin}
	className="fixed {zIndex} {!mounted ? 'opacity-0' : ''} {!mounted || !isInteractive
		? 'pointer-events-none'
		: ''}"
>
	<div
		class="w-full h-full {closing ? 'popin-scale-out' : 'popin-elastic-in'}"
		style:transform-origin={transformOrigin}
	>
		{@render children?.()}
	</div>
</Portal>

<style>
	.popin-elastic-in {
		animation: popinElasticIn 350ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
	}

	.popin-scale-out {
		pointer-events: none;
		animation: popinScaleOut 160ms cubic-bezier(0.25, 1, 0.5, 1) forwards;
	}

	@keyframes popinElasticIn {
		0% {
			opacity: 0;
			transform: scale(0.85);
		}
		100% {
			opacity: 1;
			transform: scale(1);
		}
	}

	@keyframes popinScaleOut {
		0% {
			opacity: 1;
			transform: scale(1);
		}
		100% {
			opacity: 0;
			transform: scale(0.85);
		}
	}
</style>
